import React, { useContext, useLayoutEffect, useMemo, useState } from 'react';
import { BookmarkType } from './BookmarkDropdown';
import { FilterType } from 'components/external-reference/Filter';
import { buildBookmarkName, getNewBookmarkObj, processFiltersForBookmark, setBookmarkReferenceId } from './Bookmark';
import { Stack, Box, Button, IconButton, Chip, Tooltip, alpha } from '@mui/material';
import BookmarkForm from './BookmarkForm';
import useBundleTranslation from 'i18n';
import { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import { Popup } from 'components/common/popup/Popup';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import IconMi from 'components/common/icon/IconMi';
import { BlocksContext, IBlocksContext } from '@report-content';

export type ManagerContextType = 'bookmark' | 'combination' | 'reference';

function BookmarkListItem({
    onDeleteClick,
    onDuplicateClick,
    bookmark,
    filters,
    siblingTabs,
    onEditClick,
    hasCheckbox,
    onCheck,
    isChecked,
    link,
    isFirst,
    managerContextType,
}: {
    onDeleteClick: (id: number) => void;
    onDuplicateClick: (id: number) => void;
    bookmark: BookmarkType;
    filters: Array<FilterType>;
    siblingTabs: Array<SiblingTabType>;
    onEditClick: any;
    hasCheckbox: boolean;
    onCheck: (isChecked: boolean) => void;
    isChecked: boolean;
    link: string;
    isFirst: boolean;
    managerContextType: ManagerContextType;
}) {
    const handleDeleteClick = () => onDeleteClick(bookmark.id);
    const handleDuplicateClick = () => onDuplicateClick(bookmark.id);
    const handleEditClick = () => onEditClick(bookmark);

    const blocksContext = useContext<IBlocksContext | null>(BlocksContext);
    const name = buildBookmarkName(bookmark, filters, siblingTabs, false, blocksContext?.getFilterMask);
    const { t } = useBundleTranslation(['components/external-reference/bookmark']);

    return (
        <Stack
            direction="row"
            alignItems={'flex-start'}
            sx={{
                borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                '&:first-of-type': { borderTop: 0 },
            }}
        >
            {hasCheckbox && (
                <Box
                    sx={{
                        width: '32px',
                        height: '32px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Checkbox
                        sx={{ p: 0 }}
                        onChange={(value: any, checked: any) => {
                            onCheck(checked);
                        }}
                        checked={isChecked}
                    />
                </Box>
            )}
            <DragHandle />
            <Box flexGrow={1} sx={{ px: 1, py: '7px' }}>
                {link.length ? (
                    <a href={link} rel="noopener noreferrer" target="_blank">
                        {name}
                    </a>
                ) : (
                    name
                )}
            </Box>
            <Stack flexShrink={0} direction="row">
                {isFirst && !hasCheckbox && managerContextType == 'bookmark' && (
                    <Box sx={{ pt: '7px' }}>
                        <Chip
                            sx={{ marginLeft: '12px', borderRadius: '4px' }}
                            label="default"
                            color="success"
                            variant="outlined"
                        />
                    </Box>
                )}
                {managerContextType != 'reference' && (
                    <Tooltip title={t('edit_bookmark', { type: t(managerContextType) })}>
                        <IconButton onClick={handleEditClick}>
                            <IconMi icon={'gear'} />
                        </IconButton>
                    </Tooltip>
                )}
                {!hasCheckbox && (
                    <>
                        {managerContextType != 'reference' && (
                            <Tooltip title={t('duplicate_bookmark', { type: t(managerContextType) })}>
                                <IconButton onClick={handleDuplicateClick}>
                                    <IconMi icon={'copy'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={t('delete_bookmark', { type: t(managerContextType) })}>
                            <IconButton onClick={handleDeleteClick}>
                                <IconMi icon={'delete'} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Stack>
        </Stack>
    );
}
const DragHandle = SortableHandle(() => (
    <Box sx={{ p: 1, display: 'inline-flex' }}>
        <IconMi icon={'drag-and-drop-grid'} fontSize="16" />
    </Box>
));

const SortableItem = SortableElement(
    ({
        filters,
        siblingTabs,
        onDeleteClick,
        onDuplicateClick,
        selectedBookmarks,
        hasCheckbox,
        bookmark,
        handleCheck,
        handleEditClick,
        isFirst,
        link,
        managerContextType,
    }: {
        filters: Array<FilterType>;
        siblingTabs: Array<SiblingTabType>;
        onDeleteClick: (id: number) => void;
        onDuplicateClick: (id: number) => void;
        selectedBookmarks?: Array<number>;
        hasCheckbox: boolean;
        bookmark: BookmarkType;
        handleCheck: any;
        handleEditClick: any;
        link: string;
        isFirst: boolean;
        managerContextType: ManagerContextType;
    }) => {
        return (
            <BookmarkListItem
                managerContextType={managerContextType}
                link={link}
                hasCheckbox={hasCheckbox}
                isChecked={selectedBookmarks?.includes(bookmark.id) ?? false}
                onCheck={(isChecked: boolean) => handleCheck(bookmark.id, isChecked)}
                key={bookmark.id}
                onDeleteClick={onDeleteClick}
                onDuplicateClick={onDuplicateClick}
                onEditClick={handleEditClick}
                bookmark={bookmark}
                filters={filters}
                siblingTabs={siblingTabs}
                isFirst={isFirst}
            />
        );
    },
);

const SortableList = SortableContainer(
    ({
        elementId,
        segmentValueId,
        bookmarks,
        filters,
        siblingTabs,
        onDeleteClick,
        onDuplicateClick,
        selectedBookmarks,
        provideLink,
        hasCheckbox,
        handleCheck,
        handleEditClick,
        managerContextType,
    }: {
        elementId: number;
        segmentValueId: number;
        bookmarks: Array<BookmarkType>;
        filters: Array<FilterType>;
        siblingTabs: Array<SiblingTabType>;
        onDeleteClick: (id: number) => void;
        onDuplicateClick: (id: number) => void;
        selectedBookmarks?: Array<number>;
        provideLink?: boolean;
        hasCheckbox: boolean;
        handleCheck: any;
        handleEditClick: any;
        managerContextType: ManagerContextType;
    }) => {
        return (
            <Stack
                sx={{
                    mb: 2,
                    border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    borderRadius: 1,
                }}
            >
                {bookmarks.map((bookmark, index) => {
                    const segment = segmentValueId > 0 ? `/segment/${segmentValueId}` : '';
                    const link = provideLink ? `/extreport/${elementId}${segment}?bookmark=${bookmark.id}` : '';
                    return (
                        <SortableItem
                            key={`item-${bookmark.id}`}
                            index={index}
                            //@ts-ignore
                            isFirst={index == 0}
                            //@ts-ignore
                            handleEditClick={handleEditClick}
                            handleCheck={handleCheck}
                            items={bookmarks}
                            bookmark={bookmark}
                            link={link}
                            filters={filters}
                            siblingTabs={siblingTabs}
                            onDeleteClick={onDeleteClick}
                            onDuplicateClick={onDuplicateClick}
                            selectedBookmarks={selectedBookmarks}
                            hasCheckbox={hasCheckbox}
                            managerContextType={managerContextType}
                        />
                    );
                })}
            </Stack>
        );
    },
);

export default function BookmarkManager({
    elementId,
    segmentValueId,
    bookmarks,
    filters,
    siblingTabs,
    onDeleteClick,
    onDuplicateClick,
    onUpdateClick,
    onSortEnd,
    selectedBookmarks,
    onSelectedBookmarksChange,
    provideLink,
    bookmarkForEdit,
    setOnEditMode,
    managerContextType,
    referenceId,
}: {
    elementId: number;
    segmentValueId: number;
    bookmarks: Array<BookmarkType>;
    filters: Array<FilterType>;
    siblingTabs: Array<SiblingTabType>;
    onDeleteClick: (id: number) => void;
    onDuplicateClick: (id: number) => void;
    onUpdateClick: any;
    onSortEnd: (oldIndex: number, newIndex: number, managerContextType: ManagerContextType) => void;
    selectedBookmarks?: Array<number>;
    onSelectedBookmarksChange?: (list: Array<number>) => void;
    provideLink?: boolean;
    bookmarkForEdit?: BookmarkType;
    setOnEditMode?: (onEditMode: boolean) => void;
    managerContextType: ManagerContextType;
    referenceId?: number;
}) {
    const { t } = useBundleTranslation(['components/external-reference/bookmark']);
    const [selectedBookmark, setSelectedBookmark] = useState<BookmarkType | undefined>(bookmarkForEdit);

    const handleEditClick = function (bookmark: BookmarkType) {
        setSelectedBookmark(bookmark);
    };

    useLayoutEffect(() => {
        if (!setOnEditMode) {
            return;
        }
        setOnEditMode(selectedBookmark != undefined);
    }, [selectedBookmark]);

    const handleNewClick = function () {
        const bookmark = getNewBookmarkObj();
        bookmark.filtersList = processFiltersForBookmark(filters);
        bookmark.bookmarkType = managerContextType;
        if (referenceId && siblingTabs.length) {
            let siblingTab = siblingTabs.find((t) => t.referenceId == Number(referenceId));
            if (!siblingTab) {
                siblingTab = siblingTabs[0];
            }
            setBookmarkReferenceId(bookmark, siblingTab);
        }
        setSelectedBookmark(bookmark);
    };

    const handleSaveBookmark = function (bookmark: BookmarkType) {
        onUpdateClick(bookmark);
        setSelectedBookmark(undefined);
    };

    const handleCancelEditClick = function () {
        setSelectedBookmark(undefined);
    };

    const hasCheckbox = Array.isArray(selectedBookmarks);
    const formInPopup = hasCheckbox;

    const handleCheck = (id: number, isChecked: boolean) => {
        if (!Array.isArray(selectedBookmarks) || !onSelectedBookmarksChange) {
            return;
        }
        const list = selectedBookmarks.slice();
        if (isChecked && !list.includes(id)) {
            list.push(id);
        } else {
            const index = list.findIndex((el) => el == id);
            list.splice(index, 1);
        }
        onSelectedBookmarksChange(list);
    };

    const handleFieldsSort = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        onSortEnd(oldIndex, newIndex, managerContextType);
    };

    return (
        <span>
            {(!selectedBookmark || formInPopup) && (
                <div>
                    {bookmarks.length > 0 && (
                        <SortableList
                            //@ts-ignore
                            handleEditClick={handleEditClick}
                            handleCheck={handleCheck}
                            useDragHandle
                            helperClass={'bookmark-drag-helper'}
                            items={bookmarks}
                            onSortEnd={handleFieldsSort}
                            elementId={elementId}
                            segmentValueId={segmentValueId}
                            bookmarks={bookmarks}
                            filters={filters}
                            siblingTabs={siblingTabs}
                            onDeleteClick={onDeleteClick}
                            onDuplicateClick={onDuplicateClick}
                            selectedBookmarks={selectedBookmarks}
                            provideLink={provideLink}
                            hasCheckbox={hasCheckbox}
                            managerContextType={managerContextType}
                        />
                    )}

                    {bookmarks.length == 0 && (
                        <Box
                            sx={{
                                border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                borderRadius: 1,
                                textAlign: 'center',
                                lineHeight: '30px',
                                mb: 2,
                            }}
                        >
                            {t('empty_list', { type: t(managerContextType) })}
                        </Box>
                    )}
                    <Button variant="outlined" onClick={handleNewClick} startIcon={<IconMi icon={'new'} />}>
                        {t(managerContextType)}
                    </Button>
                </div>
            )}
            {selectedBookmark &&
                (formInPopup ? (
                    <Popup
                        settings={{ noButtons: true }}
                        open={Boolean(selectedBookmark)}
                        onHide={handleCancelEditClick}
                        maxWidth="popupXl"
                    >
                        <BookmarkForm
                            elementId={elementId}
                            managerContextType={managerContextType}
                            bookmark={selectedBookmark}
                            siblingTabs={siblingTabs}
                            filters={filters}
                            onSave={handleSaveBookmark}
                            onCancel={handleCancelEditClick}
                        />
                    </Popup>
                ) : (
                    <BookmarkForm
                        elementId={elementId}
                        managerContextType={managerContextType}
                        bookmark={selectedBookmark}
                        siblingTabs={siblingTabs}
                        filters={filters}
                        onSave={handleSaveBookmark}
                        onCancel={handleCancelEditClick}
                    />
                ))}
        </span>
    );
}
